import React, { forwardRef, useEffect, useState } from "react";
import { RxCross2 } from "react-icons/rx";
import { FaBoxOpen } from "react-icons/fa";
import CrateItem from "../crate/CrateItem";
import { useDispatch, useSelector } from "react-redux";
import { downloadMp3Zip } from "../../utils/downloadMp3Zip";
import {
  checkDownloadEligibility,
  clearCrate,
  decreaseDownloadCount,
  getSignedUrl,
  increaseDownloadCount,
} from "../../store";
import { FaRegTrashCan } from "react-icons/fa6";
import { useLoggedUser } from "../../contexts/LoggedUserContext";
import showToast from "../../utils/toastNotifications";
import { useThunk } from "../../hooks/useThunk";
import { Spin } from "antd";

const CrateSidebar = forwardRef(({ isOpen, onClose }, ref) => {
  const [progress, setProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isZipDownloading, setIsZipDownloading] = useState(false);
  const [suppressLimitBanner, setSuppressLimitBanner] = useState(false);
  const crateItems = useSelector((state) => state.crate.items);
  const maxCrateItems = 12;
  const isAtMaxCapacity = crateItems.length >= maxCrateItems;
  // const [iteration, setIteration] = useState(0);
  const dispatch = useDispatch();
  const [doCheckDownloadEligibility, isChecking] = useThunk(
    checkDownloadEligibility
  );
  const [doIncreaseDownloadCount] = useThunk(increaseDownloadCount);
  const [doDecreaseDownloadCount] = useThunk(decreaseDownloadCount);
  const [doGetSignedUrl, isGettingSignedUrl, errorGettingSignedUrl] =
    useThunk(getSignedUrl);
  const { isBlockedDownload, refreshUserDownloads, downloadedVersions } =
    useLoggedUser();

  // Check device type
  useEffect(() => {
    const checkDevice = () => {
      const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;

      const isMobileUA =
        /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ||
        navigator.userAgentData?.mobile;

      setIsMobile(isTouchDevice && isMobileUA);
    };

    checkDevice();

    window.addEventListener("resize", checkDevice);
    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  const handleCheckEligibility = async () => {
    for (const item of crateItems) {
      // Check blocked download
      if (isBlockedDownload) {
        showToast(
          "error",
          "To avoid frequent download attempts, please wait a few seconds before trying again."
        );
        return "specific-error";
      } else {
        refreshUserDownloads();
      }

      // Check track info
      if (!item.trackId || !item.editId || !item.version?.id) {
        console.log("iddddddddd", item.remixerId);

        showToast(
          "error",
          `${item.trackTitle} ${item.editTitle} track is not available for download.`
        );
        return "specific-error";
      } else {
        await refreshUserDownloads();
      }

      const result = await doCheckDownloadEligibility({
        trackId: item.trackId,
        editId: item.editId,
        remixerId: item.remixerId ? item.remixerId : null,
        versionId: item.version.id,
        checkOnly: true,
      });

      const isEligible =
        result?.success &&
        result?.response?.status === "success" &&
        result?.response?.eligibility === true;

      const downloadCount = result?.response?.downloadCount;

      if (!isEligible) {
        const errorMessage =
          result?.response?.message ||
          result?.error?.message ||
          "An error occurred while checking download eligibility.";

        showToast("error", errorMessage);
        return "specific-error";
      } else {
        await refreshUserDownloads();
      }

      // Check if audio file exists
      if (!item.version.audioFile) {
        showToast(
          "error",
          `Sorry, no audio file URL is available for ${item.trackTitle} ${item.editTitle}`
        );
        return "specific-error";
      } else {
        await refreshUserDownloads();
      }

      if (!(downloadCount <= 2)) {
        showToast(
          "error",
          `You have reached maximum download limit for ${item.trackTitle} ${item.editTitle}`
        );
        return "specific-error";
      } else {
        await refreshUserDownloads();
      }
    }

    return true;
  };

  const handleDownloadZip = async () => {
    // Prevent download on mobile
    if (isMobile) {
      //showToast("error", "ZIP download is not available on mobile devices. Please use a desktop or larger screen.");
      return;
    }

    setProgress(0);

    console.log("eligibility started");

    setSuppressLimitBanner(true);
    setIsZipDownloading(true);

    const Eligibilityresult = await handleCheckEligibility();

    if (Eligibilityresult !== true) {
      console.log("eligibilityfailed");
      if (Eligibilityresult !== "specific-error") {
        showToast("error", `Download Failed`);
      }
      setIsZipDownloading(false);
      setSuppressLimitBanner(false);
      return;
    }
    setShowProgress(true);
    setIsZipDownloading(true);
    const result = await downloadMp3Zip(
      crateItems,
      setProgress,
      doGetSignedUrl,
      doIncreaseDownloadCount
    );
    if (!result) {
      for (const item of crateItems) {
        const result = await doDecreaseDownloadCount({
          trackId: item.trackId,
          editId: item.editId,
          remixerId: item.remixerId ? item.remixerId : null,
          versionId: item.version.id,
        });
      }
      await refreshUserDownloads();
      setProgress(0);
      setShowProgress(false);
      setIsZipDownloading(false);
      setSuppressLimitBanner(false);
      return;
    }
    await refreshUserDownloads();
    setProgress(0);
    setShowProgress(false);
    showToast("success", `Downloaded successfully!`);
    handleCrateClear();
    setIsZipDownloading(false);
    setSuppressLimitBanner(false);
  };

  const handleCrateClear = () => {
    dispatch(clearCrate());
  };

  useEffect(() => {}, [crateItems]);
  return (
    <>
      {/* Overlay Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[999] bg-black bg-opacity-40 backdrop-blur-sm"
          onClick={onClose}
        />
      )}

      {/* Crate Panel */}
      <div
        ref={ref}
        className={`fixed top-0 right-0 w-[82%] sm:w-[320px] md:w-[360px] lg:w-[400px] xl:w-[420px] max-w-full h-screen
        bg-[#111111] text-white z-[6000] transition-transform duration-300 ease-in-out 
        ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="flex justify-between items-center p-5 border-b border-[#1E1E1E]">
          <div className="flex">
            <h2 className="text-base sm:text-lg md:text-xl font-bold tracking-wide mr-1 flex justify-center items-center">
              YOUR CRATE
            </h2>
            <div
              className="flex text-xs border ml-1 border-gray-500 p-1 pl-2 justify-center items-center rounded-md text-gray-500 hover:cursor-pointer"
              onClick={handleCrateClear}
            >
              <FaRegTrashCan />
              <p className="p-1">Clear</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white text-lg border border-[#2A2A2A] rounded-md p-1"
          >
            <RxCross2 size={20} />
          </button>
        </div>
        {crateItems.length > 0 ? (
          <div className="flex flex-col justify-between h-[calc(100%-72px)]">
            <div className="flex flex-col overflow-y-auto overflow-x-auto">
              {isAtMaxCapacity && (
                <div className="bg-yellow-600/20 text-yellow-500 px-4 py-2 text-sm">
                  Maximum crate capacity reached (12 items)
                </div>
              )}
              {crateItems.map((item, index) => (
                <CrateItem
                  key={index}
                  track={item}
                  downloadExceeded={
                    !isZipDownloading &&
                    !suppressLimitBanner &&
                    downloadedVersions[item.version?.id || item.editId] >= 2
                  }
                />
              ))}
            </div>
            <div>
              <div
                className={` flex p-4 justify-center h-[84px] items-center bg-black text-center cursor-pointer`}
              >
                <button
                  type="button"
                  disabled={isZipDownloading}
                  aria-busy={isZipDownloading && !showProgress}
                  className={`${
                    showProgress ? "hidden" : ""
                  } mr-1 flex justify-center items-center py-3 w-full font-public-sans font-bold text-lg bg-gradient-to-t from-[#197AD0] from-[18.55%] to-[#52FAF0] to-[100%] hover:brightness-105 rounded-md focus:outline-none focus:ring-2  focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-[#1E1E1E] transition-all duration-300 ease-in-out text-[#1E1E1E] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:brightness-100
                ${isMobile ? "hidden" : ""}`}
                  onClick={handleDownloadZip}
                >
                  {isZipDownloading && !showProgress ? (
                    <>
                      <Spin size="small" className="mr-2" />
                      Preparing ZIP...
                    </>
                  ) : (
                    "Download ZIP File"
                  )}
                </button>

                <div
                  className={`flex justify-between w-[100%] ${
                    showProgress ? "" : "hidden"
                  }`}
                >
                  <div className={`w-[100%] h-3 bg-gray-700 mt-1 `}>
                    <div
                      className="h-full bg-blue-500 transition-all duration-500 ease-in-out"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <p className={`ml-1 w-14`}>{`${progress}%`}</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-[calc(100%-72px)] text-[#828282] gap-3">
            <FaBoxOpen size={40} />
            <p className="text-lg">Your crate is empty</p>
          </div>
        )}
      </div>
    </>
  );
});

export default CrateSidebar;
